import {AutogenLayoutWireCodeDetail} from "./autogen/AutogenLayoutWireCodeDetail";
import {
    ChangeEvent,
    LookupModelSearchEvent,
    DataSourceExecutionEvent,
    DataSourceAction,
    SelectionEvent,
    Textbox,
    Snackbar,
    TableRowBeforeSaveEvent
} from "@mcleod/components";
import {CurrencyUtil, Currency} from "@mcleod/core";
import {DuplicateId, DuplicateIdProps} from "@mcleod/dispatch";
import FuelInterface from "./FuelInterface";

const WireFee = {
    FIXED_FLAT_FEE: "FF",
    FIXED_PERCENTAGE: "FP",
    TIERED_FLAT_FEE: "TF",
    TIERED_PERCENTAGE: "TP",
} as const;

export class WireCodeDetail extends AutogenLayoutWireCodeDetail {

    override onLoad() {
        DuplicateId.configureIdTextbox(this.textboxWireCode, {
            tableName: "wirecode",
            screenName: "Wire Code Detail"
        } as DuplicateIdProps);

        this.mainDataSource.addBeforeExecutionListener((event: DataSourceExecutionEvent) => {
            if (event.getAction() === DataSourceAction.ADD || event.getAction() === DataSourceAction.UPDATE) {
                const validationError = this.validateAdvanceLimits();
                if (validationError) {
                    Snackbar.showWarningSnackbar(validationError);
                    event.preventDefault();
                }
            }
        });
    }


    sourceWirecodeDetailAfterExecution(event: DataSourceExecutionEvent) {
        this.toggleSubAccountContractNumPanels();
        this.showHideFieldsForSwitchCreateDeduction();
        this.showHideFieldsForSwitchCreateOtherCharge();
    }

    switchCreateDeductionOnChange(event: ChangeEvent) {
        this.showHideFieldsForSwitchCreateDeduction();
    }

    private showHideFieldsForSwitchCreateDeduction() {
        const createDeductionChecked = this.switchCreateDeduction.checked;
        this.textboxDeductCodeId.visible = createDeductionChecked;
        this.textboxDeductCodeId.required = createDeductionChecked;
        this.textboxDeductStatus.visible = createDeductionChecked;
        this.textboxDeductStatus.required = createDeductionChecked;
    }

    textboxFuelInterfaceIdOnChange(event: ChangeEvent) {
        this.toggleSubAccountContractNumPanels();
    }

    private toggleSubAccountContractNumPanels() {
        const fuelInterfaceId = this.activeRow?.get("fuel_interface_id");
        this.tabSubaccounts.visible = fuelInterfaceId === FuelInterface.COMDATA;
        this.tabContracts.visible = fuelInterfaceId === FuelInterface.EFS;
    }

    switchCreateOtherChargeOnChange(event: ChangeEvent) {
        this.showHideFieldsForSwitchCreateOtherCharge();
    }

    private showHideFieldsForSwitchCreateOtherCharge() {
        const createOtherChargeChecked = this.switchCreateOtherCharge.checked;
        this.textboxChargeCodeId.visible = createOtherChargeChecked;
        this.textboxChargeCodeId.required = createOtherChargeChecked;
        this.textboxUnpostedWireStatus.visible = createOtherChargeChecked;
        this.textboxUnpostedWireStatus.required = createOtherChargeChecked;
        this.switchOtherChargeIncludeProvider.visible = createOtherChargeChecked;
        this.mainDataSource.activeRow?.set("unposted_wire_status", "H");
        this.mainDataSource.activeRow?.set("deduct_status", "H");
    }

    filterByFuelInterface(event: LookupModelSearchEvent) {
        event.filter.fuel_interface_id = this.mainDataSource.activeRow?.get("fuel_interface_id");
    }

    textboxCompanyLimitMethodAfterDropdownSelection(event: SelectionEvent) {
        const method = this.activeRow?.get("company_limit_method");
        this.updateAdvanceLimitFieldRequirements("company", method);
    }

    textboxOwneropLimitMethodAfterDropdownSelection(event: SelectionEvent) {
        const method = this.activeRow?.get("ownerop_limit_method");
        this.updateAdvanceLimitFieldRequirements("ownerop", method);
    }

    textboxCarrierLimitMethodAfterDropdownSelection(event: SelectionEvent) {
        const method = this.activeRow?.get("carrier_limit_method");
        this.updateAdvanceLimitFieldRequirements("carrier", method);
    }

    /**
     * Updates field requirements and clears fields based on the selected limit method.
     * Adapted from RowWireCode.validateFields logic.
     *
     * @param payeeType - "company", "ownerop", or "carrier"
     * @param method - "N" (Unlimited), "F" (Flat per day), "P" (% of freight/pay), "T" (% of total)
     */
    private updateAdvanceLimitFieldRequirements(payeeType: string, method: string) {
        const amountField = this[`textbox${this.capitalize(payeeType)}LimitAmount`] as Textbox;
        const pctField = this[`textbox${this.capitalize(payeeType)}OrderPctLimit`] as Textbox;

        if (!amountField || !pctField) {
            return;
        }

        if (method === "F") {
            amountField.required = true;
            pctField.required = false;
            this.activeRow?.set(`${payeeType}_order_pct_limit`, null);
        } else if (method === "P" || method === "T") {
            pctField.required = true;
            amountField.required = false;
            this.activeRow?.set(`${payeeType}_limit_amount`, null);
        } else {
            amountField.required = false;
            pctField.required = false;
            this.activeRow?.set(`${payeeType}_order_pct_limit`, null);
            this.activeRow?.set(`${payeeType}_limit_amount`, null);
        }
    }

    /**
     * Validates advance limit fields before save.
     * Ensures that when a limit method is selected, the corresponding amount/percentage is provided and non-zero.
     * Returns error message if validation fails, null if validation passes.
     */
    private validateAdvanceLimits(): string | null {
        const activeRow = this.activeRow;
        if (!activeRow) return null;

        // Validate Company Driver Limits
        const companyMethod = activeRow.getString("company_limit_method");
        if (companyMethod === "F") {
            const amount = activeRow.getCurrency("company_limit_amount");
            if (!amount || amount.amount === 0) {
                return "Company daily amount must be entered.";
            }
        } else if (companyMethod === "P" || companyMethod === "T") {
            const pct = activeRow.getNumber("company_order_pct_limit");
            if (!pct || pct === 0) {
                return "Company order % must be entered.";
            }
        }

        // Validate Owner Operator Limits
        const owneropMethod = activeRow.getString("ownerop_limit_method");
        if (owneropMethod === "F") {
            const amount = activeRow.getCurrency("ownerop_limit_amount");
            if (!amount || amount.amount === 0) {
                return "Owner operator daily amount must be entered.";
            }
        } else if (owneropMethod === "P" || owneropMethod === "T") {
            const pct = activeRow.getNumber("ownerop_order_pct_limit");
            if (!pct || pct === 0) {
                return "Owner operator order % must be entered.";
            }
        }

        // Validate Carrier Limits
        const carrierMethod = activeRow.getString("carrier_limit_method");
        if (carrierMethod === "F") {
            const amount = activeRow.getCurrency("carrier_limit_amount");
            if (!amount || amount.amount === 0) {
                return "Carrier daily amount must be entered.";
            }
        } else if (carrierMethod === "P" || carrierMethod === "T") {
            const pct = activeRow.getNumber("carrier_order_pct_limit");
            if (!pct || pct === 0) {
                return "Carrier order % must be entered.";
            }
        }

        return null;
    }

    private capitalize(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    textboxChargeCodeIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.charge_rate_method = "F";
    }

    textboxApGlidBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.isactive = 'Y';
    }

    textboxExpenseGlidBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.isactive = 'Y';
    }

    textboxFeeTypeAfterDropdownSelection(event: SelectionEvent) {
        const quickAddRow = this.tableWireFeeStructure.quickAddRow;
        if (!quickAddRow) return;

        const textboxFeeType = quickAddRow.findComponentById("textboxFeeType") as Textbox;
        const feeType = textboxFeeType?.selectedItem?.value;

        const feeComponents = {
            wireCharge: quickAddRow.findComponentById("textboxWireCharge") as Textbox,
            amount: quickAddRow.findComponentById("textboxAmount") as Textbox,
            percentFee: quickAddRow.findComponentById("textboxPercentFee") as Textbox,
            startOfTier: quickAddRow.findComponentById("textboxStartOfTier") as Textbox,
            endOfTier: quickAddRow.findComponentById("textboxEndOfTier") as Textbox,
        };

        this.applyFeeTypeConfiguration(feeType, feeComponents);
    }

    private applyFeeTypeConfiguration(
        feeType: string,
        feeComponents: {
            wireCharge?: Textbox;
            amount?: Textbox;
            percentFee?: Textbox;
            startOfTier?: Textbox;
            endOfTier?: Textbox;
        }
    ) {
        this.resetFeeComponents(feeComponents);

        switch (feeType) {
            case WireFee.FIXED_FLAT_FEE:
                this.activateFeeComponents([feeComponents.wireCharge, feeComponents.amount]);
                break;
            case WireFee.FIXED_PERCENTAGE:
                this.activateFeeComponents([feeComponents.wireCharge, feeComponents.percentFee]);
                break;
            case WireFee.TIERED_FLAT_FEE:
                this.activateFeeComponents([
                    feeComponents.wireCharge,
                    feeComponents.amount,
                    feeComponents.startOfTier,
                    feeComponents.endOfTier
                ]);
                break;
            case WireFee.TIERED_PERCENTAGE:
                this.activateFeeComponents([
                    feeComponents.wireCharge,
                    feeComponents.percentFee,
                    feeComponents.startOfTier,
                    feeComponents.endOfTier
                ]);
                break;
        }
    }

    private resetFeeComponents(feeComponents: {
        wireCharge?: Textbox;
        amount?: Textbox;
        percentFee?: Textbox;
        startOfTier?: Textbox;
        endOfTier?: Textbox;
    }) {
        Object.values(feeComponents).forEach(component => {
            component?.clear();
            component?.resetValidation();
            if (component) {
                component.required = false;
                component.enabled = false;
            }
        });
    }

    private activateFeeComponents(components: Textbox[]) {
        components.forEach(component => {
            if (component) {
                component.enabled = true;
                component.required = true;
            }
        });
    }


    private tableWireFeeStructureBeforeRowSave(event: TableRowBeforeSaveEvent) {
        const quickAddRow = this.tableWireFeeStructure.quickAddRow;
        if (!quickAddRow) return;

        const textboxFeeType = quickAddRow.findComponentById("textboxFeeType") as Textbox;
        const textboxStartOfTier = quickAddRow.findComponentById("textboxStartOfTier") as Textbox;
        const textboxEndOfTier = quickAddRow.findComponentById("textboxEndOfTier") as Textbox;

        const quickAddFeeType = textboxFeeType?.valueAsString;

        if (WireFee.TIERED_FLAT_FEE === quickAddFeeType || WireFee.TIERED_PERCENTAGE === quickAddFeeType) {
            if (!textboxStartOfTier.validateSimple(true, false) || !textboxEndOfTier.validateSimple(true, false)) return;
            const newStartOfTier = CurrencyUtil.getCurrencyObjectFromDataValue(textboxStartOfTier?.valueAsString);
            const newEndOfTier = CurrencyUtil.getCurrencyObjectFromDataValue(textboxEndOfTier?.valueAsString);

            if (!newStartOfTier || !newEndOfTier || !quickAddFeeType) return;

            if (this.checkForStartGreaterThanEnd(newStartOfTier, newEndOfTier)) {
                textboxStartOfTier?.clear();
                textboxEndOfTier?.clear();
                Snackbar.showWarningSnackbar("Start of tier must be less than end of tier.");
            }

            // Check for overlapping ranges with existing rows of the same fee type
            if (this.checkForOverlappingRanges(quickAddFeeType, newStartOfTier, newEndOfTier)) {
                textboxStartOfTier?.clear();
                textboxEndOfTier?.clear();
                Snackbar.showWarningSnackbar("Tiered ranges must not overlap");
            }
        }
    }

    private checkForOverlappingRanges(quickAddFeeType: string, newStartOfTier: Currency, newEndOfTier: Currency): boolean {
        for (const row of this.tableWireFeeStructure.rows) {
            const feeType = row?.data?.get("fee_type");
            if (feeType !== quickAddFeeType) continue;

            const existingStart: Currency = row?.data?.getCurrency("start_of_tier");
            const existingEnd: Currency = row?.data?.getCurrency("end_of_tier");

            if (existingStart == null || existingEnd == null) continue;

            const overlaps = newStartOfTier.amount <= existingEnd.amount && existingStart.amount <= newEndOfTier.amount;

            if (overlaps) {
                return true;
            }
        }
        return false;
    }

    private checkForStartGreaterThanEnd(newStartOfTier: Currency, newEndOfTier: Currency): boolean {
        return newStartOfTier.amount >= newEndOfTier.amount;
    }
}
